// 1-D Dynamic Programming — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/backtracking.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the recurrence.

const dp1dProblems = [
  {
    id: 'climbing-stairs',
    title: 'Climbing Stairs',
    difficulty: 'Easy',
    leetcode: { number: 70, url: 'https://leetcode.com/problems/climbing-stairs/' },
    statement: 'You are climbing a staircase of n steps. Each move you can climb either 1 or 2 steps. In how many distinct ways can you reach the top?',
    example: {
      input: 'n = 5',
      output: '8',
      note: 'Every distinct sequence of 1-steps and 2-steps that sums to exactly 5.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "Directly translate \"ways to reach step n = ways to reach n-1, plus ways to reach n-2\" into recursive " +
          "calls. Correct, but this is literally the naive Fibonacci recursion — the same smaller subproblem gets " +
          "solved from scratch by many different call paths, doubling the work with every extra step.",
        steps: [
          'Base case: reaching step 0 or step 1 has exactly 1 way.',
          'Otherwise, the ways to reach step n is the ways to reach n-1 (then take a 1-step) plus the ways to reach n-2 (then take a 2-step).',
          'Recurse on both, and add.',
          'The same smaller value of n gets recomputed independently many times as the recursion branches.',
        ],
        code:
`int climbStairsBruteForce(int n) {
    if (n <= 2) return n;
    return climbStairsBruteForce(n - 1) + climbStairsBruteForce(n - 2);
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
        whenToUse: "Useful only to see the recurrence directly — the exponential blow-up from recomputing the same subproblem repeatedly is exactly what bottom-up DP eliminates.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up DP',
        why:
          "Build the answer forward from the base cases, computing each dp[i] exactly ONCE. This is the same " +
          "recurrence as the brute force, just computed in the opposite direction — bottom-up instead of top-down " +
          "— so nothing is ever solved twice.",
        steps: [
          'dp[1] = 1, dp[2] = 2 — the two base cases.',
          'For each step i from 3 to n, dp[i] = dp[i-1] + dp[i-2].',
          'Every dp[i] is computed once, using only the two values immediately before it.',
          'Return dp[n].',
        ],
        visual: {
          arrays: [{ label: 'dp[1..5] — number of ways to reach step i', values: [1, 2] }],
          varNames: [],
          steps: [
            { note: 'dp[1] = 1, dp[2] = 2 — base cases: one way to reach step 1 (a single 1-step), two ways to reach step 2 (two 1-steps, or one 2-step).' },
            { snapshots: { 'dp[1..5] — number of ways to reach step i': [1, 2, 3] }, highlights: { 'dp[1..5] — number of ways to reach step i': [2] }, note: 'dp[3] = dp[2] + dp[1] = 2 + 1 = 3.' },
            { snapshots: { 'dp[1..5] — number of ways to reach step i': [1, 2, 3, 5] }, highlights: { 'dp[1..5] — number of ways to reach step i': [3] }, note: 'dp[4] = dp[3] + dp[2] = 3 + 2 = 5.' },
            { snapshots: { 'dp[1..5] — number of ways to reach step i': [1, 2, 3, 5, 8] }, highlights: { 'dp[1..5] — number of ways to reach step i': [4] }, note: 'dp[5] = dp[4] + dp[3] = 5 + 3 = 8 — final answer for n = 5.' },
          ],
        },
        code:
`int climbStairs(int n) {
    if (n <= 2) return n;
    int[] dp = new int[n + 1];
    dp[1] = 1; dp[2] = 2;
    for (int i = 3; i <= n; i++) {
        dp[i] = dp[i - 1] + dp[i - 2];
    }
    return dp[n];
}`,
        complexity: { time: 'O(n)', space: 'O(n) — or O(1) with two rolling variables instead of a full array' },
        interviewNote: "Try the \"Climbing Stairs\" mode in this pattern's interactive playground to watch dp[i] fill in for any n from 1 to 12.",
        whenToUse: "The canonical introduction to 1-D DP — the recurrence is literally the Fibonacci sequence, and nearly every other 1-D DP problem is a variation on \"today's answer depends on a fixed few of yesterday's answers.\"",
      },
    ],
  },
  {
    id: 'house-robber',
    title: 'House Robber',
    difficulty: 'Medium',
    leetcode: { number: 198, url: 'https://leetcode.com/problems/house-robber/' },
    statement: 'Given an array of non-negative house values along one street, find the maximum total you can rob without ever robbing two directly adjacent houses.',
    example: {
      input: 'nums = [2, 7, 9, 3, 1]',
      output: '12',
      note: 'Rob houses at indices 0, 2, 4: 2 + 9 + 1 = 12.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "At every house, recursively try both choices — rob it (and skip forward past its neighbor) or skip it " +
          "— and take the better result. Correct, but \"the best result starting from house i\" is asked repeatedly " +
          "by many different earlier decisions.",
        steps: [
          'Base case: past the last house, there\'s nothing left to rob — return 0.',
          'rob = nums[i] + best result starting from house i + 2 (skip the adjacent neighbor).',
          'skip = best result starting from house i + 1.',
          'Return the larger of the two, recursing from house 0.',
        ],
        code:
`int robBruteForce(int[] nums, int i) {
    if (i >= nums.length) return 0;
    int rob = nums[i] + robBruteForce(nums, i + 2);
    int skip = robBruteForce(nums, i + 1);
    return Math.max(rob, skip);
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
        whenToUse: "Only useful to confirm the rob/skip choice at each house — without memoization, \"the best result from house i onward\" is recomputed from scratch by both of its callers.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up DP',
        why:
          "Build dp[i] — the max loot possible using only houses 0..i — forward, one house at a time. Every " +
          "dp[i] only ever needs dp[i-1] and dp[i-2], so each is computed exactly once.",
        steps: [
          'dp[0] = nums[0] — only one house, rob it.',
          'dp[1] = max(nums[0], nums[1]) — rob whichever of the first two houses is worth more.',
          'For each house i from 2 onward, dp[i] = max(dp[i-1] (skip house i), dp[i-2] + nums[i] (rob house i)).',
          'Return dp[n-1].',
        ],
        visual: {
          arrays: [{ label: 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])', values: [2, 7] }],
          varNames: [],
          steps: [
            { note: 'dp[0] = nums[0] = 2. dp[1] = max(nums[0], nums[1]) = max(2, 7) = 7.' },
            { snapshots: { 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])': [2, 7, 11] }, highlights: { 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])': [2] }, note: 'dp[2] = max(dp[1], dp[0] + nums[2]) = max(7, 2 + 9) = 11 — robbing house 2 wins.' },
            { snapshots: { 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])': [2, 7, 11, 11] }, highlights: { 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])': [3] }, note: 'dp[3] = max(dp[2], dp[1] + nums[3]) = max(11, 7 + 3) = 11 — skipping house 3 wins.' },
            { snapshots: { 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])': [2, 7, 11, 11, 12] }, highlights: { 'dp[0..4] — max loot robbable through house i (nums=[2,7,9,3,1])': [4] }, note: 'dp[4] = max(dp[3], dp[2] + nums[4]) = max(11, 11 + 1) = 12 — final answer.' },
          ],
        },
        code:
`int rob(int[] nums) {
    int n = nums.length;
    if (n == 1) return nums[0];
    int[] dp = new int[n];
    dp[0] = nums[0];
    dp[1] = Math.max(nums[0], nums[1]);
    for (int i = 2; i < n; i++) {
        dp[i] = Math.max(dp[i - 1], dp[i - 2] + nums[i]);
    }
    return dp[n - 1];
}`,
        complexity: { time: 'O(n)', space: 'O(n) — or O(1) with two rolling variables' },
        interviewNote: "Try the \"House Robber\" mode in this pattern's interactive playground with any list of house values.",
        whenToUse: "The template \"skip vs. take-and-jump-back-two\" recurrence reappears constantly — Delete and Earn and House Robber II both reduce directly to this exact shape.",
      },
    ],
  },
  {
    id: 'house-robber-ii',
    title: 'House Robber II',
    difficulty: 'Medium',
    leetcode: { number: 213, url: 'https://leetcode.com/problems/house-robber-ii/' },
    statement: 'Same as House Robber, but the houses are arranged in a CIRCLE — the first and last houses are now also adjacent, so robbing both is forbidden.',
    example: {
      input: 'nums = [2, 3, 2]',
      output: '3',
      note: 'Robbing house 0 AND house 2 is illegal here (they\'re circularly adjacent) — the best legal choice is house 1 alone: 3.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Every Subset, Checking the Wrap-Around Edge)',
        why:
          "Try every one of the 2ⁿ possible subsets of houses, and keep only the ones where no two ADJACENT " +
          "houses are both included — critically, treating house 0 and house n-1 as adjacent too, since the " +
          "street wraps into a circle. Correct, but checking every subset is exponential.",
        steps: [
          'For every bitmask from 0 to 2ⁿ - 1, treat each set bit as \"this house is robbed.\"',
          'Reject the mask if any two adjacent houses are both robbed — including the wrap-around pair (last house, house 0).',
          'For every valid mask, sum the robbed houses\' values.',
          'Track the maximum sum found across all valid masks.',
        ],
        code:
`int robBruteForce(int[] nums) {
    int n = nums.length;
    if (n == 1) return nums[0];
    int best = 0;
    for (int mask = 0; mask < (1 << n); mask++) {
        if (isValidCircular(mask, n)) {
            int sum = 0;
            for (int i = 0; i < n; i++) if ((mask & (1 << i)) != 0) sum += nums[i];
            best = Math.max(best, sum);
        }
    }
    return best;
}

private boolean isValidCircular(int mask, int n) {
    for (int i = 0; i < n; i++) {
        int next = (i + 1) % n;   // wraps the last house back to house 0
        if ((mask & (1 << i)) != 0 && (mask & (1 << next)) != 0) return false;
    }
    return true;
}`,
        complexity: { time: 'O(2ⁿ · n)', space: 'O(1)' },
        whenToUse: "Only useful to make the circular constraint fully explicit — checking every subset (including the wrap-around edge between the last and first house) is exponential and impractical beyond tiny inputs.",
      },
      {
        name: 'Approach 2 — Optimized: Split Into Two Linear House Robber Runs',
        why:
          "The circle constraint only ever forbids ONE specific pair: robbing both house 0 and house n-1. So " +
          "either house 0 is excluded entirely, or house n-1 is excluded entirely — and each of those two cases " +
          "is exactly the LINEAR House Robber problem, already solved, run on a sub-range.",
        steps: [
          'Case A: exclude the last house entirely — run linear House Robber on indices 0..n-2.',
          'Case B: exclude the first house entirely — run linear House Robber on indices 1..n-1.',
          'Since a solution can\'t use BOTH excluded houses at once, one of these two cases always contains the true optimal answer.',
          'Return the larger of the two results.',
        ],
        visual: {
          arrays: [{ label: 'houses (nums = [2, 3, 2])', values: [2, 3, 2] }],
          varNames: ['caseA', 'caseB'],
          steps: [
            { highlights: { 'houses (nums = [2, 3, 2])': [0, 1] }, vars: { caseA: 3 }, note: 'Case A — exclude the last house (index 2): run linear House Robber on indices 0..1 = [2, 3]. It rolls out to max(2, 3) = 3.' },
            { highlights: { 'houses (nums = [2, 3, 2])': [1, 2] }, vars: { caseA: 3, caseB: 3 }, note: 'Case B — exclude the first house (index 0): run linear House Robber on indices 1..2 = [3, 2]. It rolls out to max(3, 0 + 2) = 3.' },
            { vars: { caseA: 3, caseB: 3 }, note: 'Answer = max(caseA, caseB) = max(3, 3) = 3 — matches the expected output for nums = [2, 3, 2].' },
          ],
        },
        code:
`int rob(int[] nums) {
    int n = nums.length;
    if (n == 1) return nums[0];
    return Math.max(robLinear(nums, 0, n - 2), robLinear(nums, 1, n - 1));
}

private int robLinear(int[] nums, int start, int end) {
    int prev2 = 0, prev1 = 0;
    for (int i = start; i <= end; i++) {
        int cur = Math.max(prev1, prev2 + nums[i]);
        prev2 = prev1;
        prev1 = cur;
    }
    return prev1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "This reduction — recognizing that a circular constraint splits cleanly into two linear sub-cases — is a very common interview follow-up to House Robber.",
        whenToUse: "Whenever a circular constraint forbids using both \"ends\" together, split into two linear subproblems that each exclude one end, solve the already-known linear version on each, and combine with a max.",
      },
    ],
  },
  {
    id: 'decode-ways',
    title: 'Decode Ways',
    difficulty: 'Medium',
    leetcode: { number: 91, url: 'https://leetcode.com/problems/decode-ways/' },
    statement: 'A message of digits can be decoded back to letters using \'A\'=1 through \'Z\'=26. Given a digit string, count the total number of ways it can be decoded.',
    example: {
      input: 's = "226"',
      output: '3',
      note: '"2,2,6" → "BBF", "22,6" → "VF", "2,26" → "BZ" — three valid decodings.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "At each position, recursively try consuming one digit or two digits (whenever the two-digit cut forms " +
          "a valid 10-26 code), and sum the ways from each. Correct, but \"the number of ways to decode the SUFFIX " +
          "starting at position i\" gets independently re-solved by every different earlier path that reaches it.",
        steps: [
          'Base case: reaching the end of the string is exactly 1 valid (empty) decoding.',
          'If the current digit is \'0\', no decoding starts here — return 0 immediately (0 has no letter mapping alone).',
          'Always count decodings that take exactly one digit.',
          'If the next two digits form a number from 10 to 26, ALSO count decodings that take exactly two digits.',
        ],
        code:
`int numDecodingsBruteForce(String s, int i) {
    if (i == s.length()) return 1;
    if (s.charAt(i) == '0') return 0;
    int ways = numDecodingsBruteForce(s, i + 1);
    if (i + 1 < s.length()) {
        int twoDigit = Integer.parseInt(s.substring(i, i + 2));
        if (twoDigit <= 26) ways += numDecodingsBruteForce(s, i + 2);
    }
    return ways;
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
        whenToUse: "Only useful to pin down exactly when a one-digit vs. two-digit cut is legal — without memoization, the same suffix gets re-decoded from scratch by every path that reaches it.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up DP',
        why:
          "Build dp[i] — the number of ways to decode the PREFIX of length i — forward. Each dp[i] only ever " +
          "depends on dp[i-1] (one-digit cut) and dp[i-2] (two-digit cut), gated by whether that specific cut is " +
          "actually a legal code.",
        steps: [
          'dp[0] = 1 (the empty prefix has exactly one — trivial — decoding) and dp[1] = 1 if the first character isn\'t \'0\'.',
          'For each position i from 2 to n, check the single digit s[i-1]: if it\'s not \'0\', add dp[i-1].',
          'Check the two-digit number s[i-2..i]: if it falls between 10 and 26, ALSO add dp[i-2].',
          'Return dp[n].',
        ],
        visual: {
          arrays: [{ label: 'dp[0..3] — ways to decode s[0..i) for s="226"', values: [1, 1] }],
          varNames: [],
          steps: [
            { snapshots: { 'dp[0..3] — ways to decode s[0..i) for s="226"': [1, 1, 2] }, highlights: { 'dp[0..3] — ways to decode s[0..i) for s="226"': [2] }, note: 'dp[2]: s[1]="2" (nonzero) → +dp[1]=1. s[0:2]="22" (between 10-26) → +dp[0]=1. dp[2] = 1 + 1 = 2.' },
            { snapshots: { 'dp[0..3] — ways to decode s[0..i) for s="226"': [1, 1, 2, 3] }, highlights: { 'dp[0..3] — ways to decode s[0..i) for s="226"': [3] }, note: 'dp[3]: s[2]="6" (nonzero) → +dp[2]=2. s[1:3]="26" (between 10-26) → +dp[1]=1. dp[3] = 2 + 1 = 3 — final answer.' },
          ],
        },
        code:
`int numDecodings(String s) {
    int n = s.length();
    if (n == 0 || s.charAt(0) == '0') return 0;
    int prev2 = 1, prev1 = 1;   // dp[0] = 1 (empty), dp[1] = 1 (first char already validated non-zero)
    for (int i = 2; i <= n; i++) {
        int cur = 0;
        if (s.charAt(i - 1) != '0') cur += prev1;
        int twoDigit = Integer.parseInt(s.substring(i - 2, i));
        if (twoDigit >= 10 && twoDigit <= 26) cur += prev2;
        prev2 = prev1;
        prev1 = cur;
    }
    return prev1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Watch how a leading-zero substring (like \"06\") is silently excluded — Integer.parseInt still reads it as 6 (which is < 10), so the two-digit branch is correctly rejected.",
        whenToUse: "The classic \"count the ways\" 1-D DP with a validity twist — the same Climbing-Stairs-shaped recurrence, but each contribution is gated by whether that specific cut is actually a legal decode.",
      },
    ],
  },
  {
    id: 'coin-change',
    title: 'Coin Change',
    difficulty: 'Medium',
    leetcode: { number: 322, url: 'https://leetcode.com/problems/coin-change/' },
    statement: 'Given coin denominations and a target amount, return the fewest number of coins needed to make up that amount (unlimited supply of each coin), or -1 if it\'s impossible.',
    example: {
      input: 'coins = [1, 2, 5], amount = 11',
      output: '3',
      note: '11 = 5 + 5 + 1, using exactly 3 coins — the fewest possible.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "At each remaining amount, recursively try every coin, and take 1 + the best result on what's left. " +
          "Correct, but \"the fewest coins for THIS remaining amount\" is asked over and over by many different " +
          "coin orderings that happen to arrive at the same remainder.",
        steps: [
          'Base case: amount 0 needs 0 coins.',
          'If the amount goes negative, this path overshot — treat it as impossible.',
          'Otherwise, try every coin: pay 1 coin, then recursively solve for amount - coin.',
          'Take the minimum result across all coins tried; the same remaining amount is repeatedly re-solved via different coin sequences.',
        ],
        code:
`int coinChangeBruteForce(int[] coins, int amount) {
    if (amount == 0) return 0;
    if (amount < 0) return Integer.MAX_VALUE;
    int best = Integer.MAX_VALUE;
    for (int coin : coins) {
        int sub = coinChangeBruteForce(coins, amount - coin);
        if (sub != Integer.MAX_VALUE) best = Math.min(best, sub + 1);
    }
    return best;
}`,
        complexity: { time: 'Exponential — the same remaining amount is repeatedly re-solved', space: 'O(amount) — recursion depth' },
        whenToUse: "Only useful to confirm the recurrence itself — without memoization, the overlapping remaining-amount subproblems make this impractical beyond small amounts.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up DP (Unbounded Knapsack)',
        why:
          "Build dp[0..amount] forward, where dp[i] is the fewest coins to make amount i. Unlike 0/1 knapsack, " +
          "each coin can be reused any number of times — which is exactly why the loop tries every COIN at each " +
          "fixed amount, rather than the other way around. Each dp[i] is finalized exactly once.",
        steps: [
          'dp[0] = 0 — zero coins needed to make amount 0. Every other dp[i] starts at \"unreachable\" (infinity).',
          'For each amount from 1 up to the target, try every coin ≤ that amount.',
          'dp[amount] = 1 + the smallest dp[amount - coin] found across all valid coins.',
          'Return dp[target], or -1 if it\'s still unreachable.',
        ],
        visual: {
          arrays: [{ label: 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])', values: [0, 1, null, null, null, null, null, null, null, null, null, null] }],
          varNames: [],
          steps: [
            { note: 'dp[0] = 0. dp[1]: only coin 1 fits → dp[0] + 1 = 1.' },
            { snapshots: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [0, 1, 1, null, null, null, null, null, null, null, null, null] }, highlights: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [2] }, note: 'dp[2]: coin1 → dp[1]+1=2; coin2 → dp[0]+1=1. Best: dp[2] = 1.' },
            { snapshots: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [0, 1, 1, 2, null, null, null, null, null, null, null, null] }, highlights: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [3] }, note: 'dp[3]: coin1 → dp[2]+1=2; coin2 → dp[1]+1=2. Best: dp[3] = 2.' },
            { snapshots: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [0, 1, 1, 2, 2, null, null, null, null, null, null, null] }, highlights: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [4] }, note: 'dp[4]: coin1 → dp[3]+1=3; coin2 → dp[2]+1=2. Best: dp[4] = 2.' },
            { snapshots: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [0, 1, 1, 2, 2, 1, null, null, null, null, null, null] }, highlights: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [5] }, note: 'dp[5]: coin1 → dp[4]+1=3; coin2 → dp[3]+1=3; coin5 → dp[0]+1=1. Best: dp[5] = 1 (a single 5-coin).' },
            { snapshots: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [0, 1, 1, 2, 2, 1, 2, 2, 3, 3, 2, null] }, highlights: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [10] }, note: 'Continuing the same way for amounts 6–10: dp[6]=2, dp[7]=2, dp[8]=3, dp[9]=3, dp[10]=2 (two 5-coins).' },
            { snapshots: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [0, 1, 1, 2, 2, 1, 2, 2, 3, 3, 2, 3] }, highlights: { 'dp[0..11] — fewest coins for each amount (coins=[1,2,5])': [11] }, note: 'dp[11]: coin1 → dp[10]+1=3; coin2 → dp[9]+1=4; coin5 → dp[6]+1=3. Best: dp[11] = 3 — final answer, e.g. 5 + 5 + 1.' },
          ],
        },
        code:
`int coinChange(int[] coins, int amount) {
    int[] dp = new int[amount + 1];
    Arrays.fill(dp, Integer.MAX_VALUE);
    dp[0] = 0;
    for (int amt = 1; amt <= amount; amt++) {
        for (int coin : coins) {
            if (coin <= amt && dp[amt - coin] != Integer.MAX_VALUE) {
                dp[amt] = Math.min(dp[amt], dp[amt - coin] + 1);
            }
        }
    }
    return dp[amount] == Integer.MAX_VALUE ? -1 : dp[amount];
}`,
        complexity: { time: 'O(amount · coins.length)', space: 'O(amount)' },
        interviewNote: "Try the \"Coin Change\" mode in this pattern's interactive playground with any coin set and amount up to 20. Also worth mentioning out loud: this is equivalent to unweighted shortest-path BFS from amount 0, if amounts are treated as graph nodes and coins as edges.",
        whenToUse: "The textbook \"unbounded knapsack\" 1-D DP — each coin can be reused any number of times, unlike Partition Equal Subset Sum where each number is used at most once.",
      },
    ],
  },
  {
    id: 'maximum-product-subarray',
    title: 'Maximum Product Subarray',
    difficulty: 'Medium',
    leetcode: { number: 152, url: 'https://leetcode.com/problems/maximum-product-subarray/' },
    statement: 'Given an integer array, find the contiguous subarray (containing at least one number) with the largest product, and return that product.',
    example: {
      input: 'nums = [2, 3, -2, 4]',
      output: '6',
      note: 'The subarray [2, 3] has product 6 — the largest possible.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force',
        why:
          "For every possible starting index, extend the subarray one element at a time, tracking the running " +
          "product and comparing it against the best seen so far. Correct and simple, but every one of the O(n²) " +
          "subarrays gets its product computed.",
        steps: [
          'For each start index i, initialize a running product of 1.',
          'For each end index j from i onward, multiply the running product by nums[j].',
          'Compare the running product against the best found so far.',
          'After trying every (start, end) pair, the best is the answer.',
        ],
        code:
`int maxProductBruteForce(int[] nums) {
    int best = nums[0];
    for (int i = 0; i < nums.length; i++) {
        int product = 1;
        for (int j = i; j < nums.length; j++) {
            product *= nums[j];
            best = Math.max(best, product);
        }
    }
    return best;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse: "Fine for small arrays or as a sanity-check baseline — but the O(n) approach below is what's actually expected once the negative-number trick is understood.",
      },
      {
        name: 'Approach 2 — Optimized: Track Running Max AND Min',
        why:
          "A single negative number can flip the SMALLEST (most negative) running product into the LARGEST one " +
          "with just one more multiplication. So a running minimum must be tracked right alongside the running " +
          "maximum — at every step, either one could become the new maximum once multiplied by the next number.",
        steps: [
          'Track both maxProd and minProd, both starting at nums[0].',
          'At each new number, compute three candidates: the number alone, maxProd × number, and minProd × number.',
          'The new maxProd is the largest of those three candidates; the new minProd is the smallest.',
          'Update the overall best with the new maxProd at every step.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [2, 3, -2, 4] }],
          varNames: ['maxProd', 'minProd', 'best'],
          steps: [
            { highlights: { nums: [0] }, vars: { maxProd: 2, minProd: 2, best: 2 }, note: 'Start: maxProd = minProd = best = nums[0] = 2.' },
            { highlights: { nums: [1] }, vars: { maxProd: 6, minProd: 3, best: 6 }, note: 'num=3: candidates are 3, 2×3=6, 2×3=6 → new maxProd=6, minProd=3. best=max(2,6)=6.' },
            { highlights: { nums: [2] }, vars: { maxProd: -2, minProd: -12, best: 6 }, note: 'num=-2 (negative!): multiplying flips extremes — candidates are -2, 6×-2=-12, 3×-2=-6 → new maxProd=-2, minProd=-12 (the OLD maxProd, once hit by a negative, becomes the new minimum). best stays 6.' },
            { highlights: { nums: [3] }, vars: { maxProd: 4, minProd: -48, best: 6 }, note: 'num=4: candidates are 4, -2×4=-8, -12×4=-48 → new maxProd=4, minProd=-48. best stays max(6,4)=6 — final answer.' },
          ],
        },
        code:
`int maxProduct(int[] nums) {
    int maxProd = nums[0], minProd = nums[0], best = nums[0];
    for (int i = 1; i < nums.length; i++) {
        int num = nums[i];
        int candidateMax = Math.max(num, Math.max(maxProd * num, minProd * num));
        int candidateMin = Math.min(num, Math.min(maxProd * num, minProd * num));
        maxProd = candidateMax;
        minProd = candidateMin;
        best = Math.max(best, maxProd);
    }
    return best;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "A common early mistake: tracking only a running max, the way Kadane's Algorithm does for Maximum SUBARRAY (sum). Products need the running min too, because multiplying by a negative reverses order.",
        whenToUse: "Whenever a running \"best so far\" state can be REVERSED by one more operation (here, multiplication by a negative), track both extremes together — this pattern reappears anywhere sign flips matter.",
      },
    ],
  },
  {
    id: 'word-break',
    title: 'Word Break',
    difficulty: 'Medium',
    leetcode: { number: 139, url: 'https://leetcode.com/problems/word-break/' },
    statement: 'Given a string and a dictionary of words, determine if the string can be segmented into a space-separated sequence of one or more dictionary words.',
    example: {
      input: 's = "leetcode", wordDict = ["leet", "code"]',
      output: 'true',
      note: '"leetcode" splits cleanly into "leet" + "code", both in the dictionary.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "At each position, try every possible next cut, and recurse on the rest of the string if the piece " +
          "just cut off is a dictionary word. Correct, but \"can the SUFFIX starting at position i be broken\" is " +
          "re-asked from scratch by every different earlier split point that reaches position i.",
        steps: [
          'Base case: reaching the end of the string means everything before it was successfully broken — return true.',
          'From the current position, try every possible end point for the next piece.',
          'If that piece is a dictionary word, recursively check whether the REST of the string (from the end point onward) can also be broken.',
          'If any split succeeds, the whole string is breakable.',
        ],
        code:
`boolean wordBreakBruteForce(String s, Set<String> dict, int start) {
    if (start == s.length()) return true;
    for (int end = start + 1; end <= s.length(); end++) {
        if (dict.contains(s.substring(start, end)) && wordBreakBruteForce(s, dict, end)) {
            return true;
        }
    }
    return false;
}`,
        complexity: { time: 'Exponential in the worst case — the same suffix gets re-checked via many split points', space: 'O(n) — recursion depth' },
        whenToUse: "Only useful to confirm the split logic — without memoizing \"can the suffix starting at position i be broken,\" overlapping suffixes get rechecked repeatedly.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up DP',
        why:
          "Build dp[i] — true if the PREFIX of length i is breakable — forward. dp[i] is true the moment ANY " +
          "earlier true prefix dp[j] is found where the piece s[j..i) is a dictionary word — every prefix is " +
          "checked exactly once.",
        steps: [
          'dp[0] = true — the empty prefix is trivially breakable.',
          'For each prefix length i from 1 to n, scan every earlier split point j < i.',
          'If dp[j] is true AND s[j..i) is in the dictionary, then dp[i] is true — stop scanning further j\'s for this i.',
          'Return dp[n].',
        ],
        visual: {
          arrays: [
            { label: 's', values: ['l', 'e', 'e', 't', 'c', 'o', 'd', 'e'] },
            { label: 'dp[0..8] (T = breakable up to index i)', values: ['T'] },
          ],
          varNames: [],
          steps: [
            { note: 'dp[0] = T (empty prefix trivially breakable).' },
            { snapshots: { 'dp[0..8] (T = breakable up to index i)': ['T', 'F', 'F', 'F', 'T'] }, highlights: { s: [0, 1, 2, 3] }, note: 'Indices 1–3 stay F (no dictionary word ends there). At i=4: j=0, dp[0]=T and s[0:4]="leet" is in the dictionary → dp[4] = T.' },
            { snapshots: { 'dp[0..8] (T = breakable up to index i)': ['T', 'F', 'F', 'F', 'T', 'F', 'F', 'F', 'T'] }, highlights: { s: [4, 5, 6, 7] }, note: 'Indices 5–7 stay F. At i=8: j=4, dp[4]=T and s[4:8]="code" is in the dictionary → dp[8] = T — the whole string is breakable. Answer: true.' },
          ],
        },
        code:
`boolean wordBreak(String s, List<String> wordDict) {
    Set<String> dict = new HashSet<>(wordDict);
    int n = s.length();
    boolean[] dp = new boolean[n + 1];
    dp[0] = true;
    for (int i = 1; i <= n; i++) {
        for (int j = 0; j < i; j++) {
            if (dp[j] && dict.contains(s.substring(j, i))) {
                dp[i] = true;
                break;
            }
        }
    }
    return dp[n];
}`,
        complexity: { time: 'O(n²) — plus substring hashing cost', space: 'O(n)' },
        interviewNote: "A HashSet for the dictionary keeps each substring lookup O(1) (amortized) instead of scanning the whole word list at every check.",
        whenToUse: "Recognize Word Break as 1-D DP the moment the question becomes \"can PREFIX-of-length-i be validly built\" — dp[i] depends on scanning back to every earlier TRUE prefix and checking if the remaining piece is a dictionary word.",
      },
    ],
  },
  {
    id: 'longest-increasing-subsequence',
    title: 'Longest Increasing Subsequence',
    difficulty: 'Medium',
    leetcode: { number: 300, url: 'https://leetcode.com/problems/longest-increasing-subsequence/' },
    statement: 'Given an integer array, return the length of the longest STRICTLY increasing subsequence (elements don\'t need to be contiguous, but must keep their original order).',
    example: {
      input: 'nums = [10, 9, 2, 5, 3, 7, 101, 18]',
      output: '4',
      note: 'One longest subsequence is [2, 3, 7, 101] (or [2, 3, 7, 18]) — length 4.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion (Include / Exclude)',
        why:
          "At each element, recursively try both including it (only if it's larger than the last included value) " +
          "and excluding it, taking the longer result. Correct — this explores every one of the 2ⁿ subsequences " +
          "— but wildly redundant, since the same (index, last-value) state recurs constantly.",
        steps: [
          'Base case: past the last element, the length contributed is 0.',
          'skip = best result from the next index, keeping the same "last included value."',
          'take = 1 + best result from the next index, but ONLY if the current element is greater than the last included value.',
          'Return the larger of skip and take.',
        ],
        code:
`int solve(int[] nums, int i, int prevVal) {
    if (i == nums.length) return 0;
    int skip = solve(nums, i + 1, prevVal);
    int take = 0;
    if (nums[i] > prevVal) {
        take = 1 + solve(nums, i + 1, nums[i]);
    }
    return Math.max(skip, take);
}
// call: solve(nums, 0, Integer.MIN_VALUE)`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
        whenToUse: "Only useful to confirm the include/exclude framing — without memoizing on (index, last value), the same states are revisited exponentially many times.",
      },
      {
        name: 'Approach 2 — Optimized: O(n²) Bottom-Up DP',
        why:
          "Define dp[i] as the length of the longest increasing subsequence that ENDS exactly at index i. Every " +
          "dp[i] only needs to look back at every earlier index j and check whether extending nums[j]'s best " +
          "subsequence with nums[i] is legal (nums[j] < nums[i]) and better than what's already found.",
        steps: [
          'Initialize every dp[i] = 1 — every single element is trivially an increasing subsequence of length 1.',
          'For each index i, scan every earlier index j < i.',
          'If nums[j] < nums[i], dp[i] can potentially become dp[j] + 1 — keep the best such value.',
          'The final answer is the LARGEST value across the entire dp array (not necessarily dp[n-1] — the longest subsequence might not end at the last element).',
        ],
        visual: {
          arrays: [{ label: 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])', values: [1, 1, 1] }],
          varNames: [],
          steps: [
            { note: 'dp[0]=dp[1]=dp[2]=1 — 10, 9, and 2 each start their own subsequence; no smaller earlier element extends any of them yet.' },
            { snapshots: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [1, 1, 1, 2] }, highlights: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [3] }, note: 'nums[3]=5: j=2 (nums[2]=2 < 5) gives dp[2]+1=2. dp[3] = 2.' },
            { snapshots: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [1, 1, 1, 2, 2] }, highlights: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [4] }, note: 'nums[4]=3: only j=2 (nums[2]=2 < 3) qualifies — dp[2]+1=2. dp[4] = 2.' },
            { snapshots: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [1, 1, 1, 2, 2, 3] }, highlights: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [5] }, note: 'nums[5]=7: best comes from j=3 or j=4 (both dp=2) → dp[5] = 2 + 1 = 3.' },
            { snapshots: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [1, 1, 1, 2, 2, 3, 4] }, highlights: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [6] }, note: 'nums[6]=101: every earlier value qualifies — best is dp[5]+1 = 4. dp[6] = 4.' },
            { snapshots: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [1, 1, 1, 2, 2, 3, 4, 4] }, highlights: { 'dp[0..7] — LIS length ending at i (nums=[10,9,2,5,3,7,101,18])': [7] }, note: 'nums[7]=18: best comes from j=5 (nums[5]=7 < 18, dp[5]=3) → dp[7] = 4. Overall max across the table: 4 — the final answer.' },
          ],
        },
        code:
`int lengthOfLIS(int[] nums) {
    int[] dp = new int[nums.length];
    Arrays.fill(dp, 1);
    int best = 1;
    for (int i = 1; i < nums.length; i++) {
        for (int j = 0; j < i; j++) {
            if (nums[j] < nums[i]) {
                dp[i] = Math.max(dp[i], dp[j] + 1);
            }
        }
        best = Math.max(best, dp[i]);
    }
    return best;
}`,
        complexity: { time: 'O(n²)', space: 'O(n)' },
        interviewNote: "Try the \"Longest Incr. Subseq.\" mode in this pattern's interactive playground to watch this exact O(n²) table fill in for any sequence.",
        whenToUse: "The standard, easiest-to-derive correct solution — always safe to lead with in an interview before optimizing further.",
      },
      {
        name: 'Approach 3 — Further Optimized: O(n log n) With Binary Search',
        why:
          "Only the SMALLEST possible tail value matters for each achievable subsequence length — if two " +
          "subsequences both have length 3, the one with the smaller last element is strictly more useful for " +
          "future extension. Tracking just those smallest tails lets binary search replace the O(n) inner scan.",
        steps: [
          'Maintain a "tails" array: tails[k] = the smallest possible tail value among all increasing subsequences of length k+1 found so far.',
          'For each new number, binary search tails for the first value ≥ the number.',
          'If found, REPLACE that position with the number (a smaller tail for that same length — strictly better for future extensions).',
          'If not found (the number is larger than every tail), APPEND it — the longest subsequence just grew by one.',
        ],
        visual: {
          arrays: [{ label: 'tails (smallest tail for each achievable length)', values: [] }],
          varNames: [],
          steps: [
            { snapshots: { 'tails (smallest tail for each achievable length)': [10] }, note: 'num=10: tails is empty, so 10 starts a length-1 subsequence. tails=[10].' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [9] }, note: 'num=9: binary search finds tails[0]=10 is the first value ≥ 9 — replace it with the smaller tail. tails=[9].' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [2] }, note: 'num=2: replaces tails[0]=9 with an even smaller tail for length 1. tails=[2].' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [2, 5] }, note: 'num=5: bigger than every tail so far — append. tails=[2, 5] (achievable length grows to 2).' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [2, 3] }, note: 'num=3: binary search finds tails[1]=5 is the first ≥ 3 — replace it. tails=[2, 3].' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [2, 3, 7] }, note: 'num=7: bigger than every tail — append. tails=[2, 3, 7] (length 3).' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [2, 3, 7, 101] }, note: 'num=101: append again. tails=[2, 3, 7, 101] (length 4).' },
            { snapshots: { 'tails (smallest tail for each achievable length)': [2, 3, 7, 18] }, note: 'num=18: binary search finds tails[3]=101 is the first ≥ 18 — replace it. tails=[2, 3, 7, 18] — length stays 4, the final answer. (tails is NOT necessarily the actual LIS itself, just bookkeeping — though here it happens to also be a valid one.)' },
          ],
        },
        code:
`int lengthOfLIS(int[] nums) {
    int[] tails = new int[nums.length];
    int size = 0;
    for (int num : nums) {
        int lo = 0, hi = size;
        while (lo < hi) {
            int mid = (lo + hi) / 2;
            if (tails[mid] < num) lo = mid + 1; else hi = mid;
        }
        tails[lo] = num;
        if (lo == size) size++;
    }
    return size;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        interviewNote: "This is the optimal solution expected once the O(n²) DP has been derived and discussed — mention it as a natural follow-up even if you implement the O(n²) version first.",
        whenToUse: "Reach for this the moment n is large enough that O(n²) is too slow, or when the interviewer explicitly asks for a faster approach after the DP solution.",
      },
    ],
  },
  {
    id: 'partition-equal-subset-sum',
    title: 'Partition Equal Subset Sum',
    difficulty: 'Medium',
    leetcode: { number: 416, url: 'https://leetcode.com/problems/partition-equal-subset-sum/' },
    statement: 'Given an array of positive integers, determine whether it can be partitioned into two subsets such that the sum of elements in both subsets is equal.',
    example: {
      input: 'nums = [1, 5, 11, 5]',
      output: 'true',
      note: 'Split into {1, 5, 5} and {11} — both sum to 11.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "If the total sum is odd, an equal split is immediately impossible. Otherwise, recursively try " +
          "including or excluding each number, checking whether SOME subset sums to exactly half the total. " +
          "Correct, but \"can some subset of the remaining numbers reach this exact remaining target\" is asked " +
          "repeatedly for the same (index, remaining) pair.",
        steps: [
          'Compute the total sum; if it\'s odd, return false immediately — no equal split is possible.',
          'Otherwise, the target for one subset is totalSum / 2.',
          'Recursively try, for each number: include it (subtract it from the remaining target) or exclude it.',
          'Return true the moment any path drives the remaining target to exactly 0.',
        ],
        code:
`boolean canReach(int[] nums, int i, int remaining) {
    if (remaining == 0) return true;
    if (i == nums.length || remaining < 0) return false;
    return canReach(nums, i + 1, remaining - nums[i]) || canReach(nums, i + 1, remaining);
}
// call: totalSum even, then canReach(nums, 0, totalSum / 2)`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
        whenToUse: "Only useful to confirm the include/exclude subset-sum framing — without memoization on (index, remaining), the same remaining target is re-checked from many different paths.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up 0/1 Knapsack DP',
        why:
          "This is Subset Sum wearing a \"partition\" costume: build a boolean dp array where dp[t] means \"some " +
          "subset of the numbers processed SO FAR sums to exactly t.\" Scanning t BACKWARDS while processing each " +
          "number guarantees that number is used at most once — the classic 0/1 knapsack safeguard.",
        steps: [
          'If the total sum is odd, return false. Otherwise the target is totalSum / 2.',
          'dp[0] = true (a sum of 0 is always reachable — pick nothing).',
          'For each number, scan the DP array from target DOWN to that number, and set dp[t] = true whenever dp[t - num] is already true.',
          'Scanning backwards prevents the SAME number from being counted twice within a single pass. Return dp[target].',
        ],
        visual: {
          arrays: [{ label: 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])', values: ['T', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F'] }],
          varNames: [],
          steps: [
            { note: 'Total sum = 22 (even) → target = 11. dp[0] = T to start; every other sum begins unreachable.' },
            { snapshots: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': ['T', 'T', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F', 'F'] }, highlights: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': [1] }, note: 'Process num=1: scanning t from 11 down to 1, dp[1] becomes reachable via dp[0]. Reachable sums: {0, 1}.' },
            { snapshots: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': ['T', 'T', 'F', 'F', 'F', 'T', 'T', 'F', 'F', 'F', 'F', 'F'] }, highlights: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': [5, 6] }, note: 'Process num=5: dp[5] (via dp[0]) and dp[6] (via dp[1]) become reachable. Reachable sums: {0, 1, 5, 6}.' },
            { snapshots: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': ['T', 'T', 'F', 'F', 'F', 'T', 'T', 'F', 'F', 'F', 'F', 'T'] }, highlights: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': [11] }, note: 'Process num=11: dp[11] becomes reachable directly via dp[0] + 11. The target (11) is now reachable!' },
            { snapshots: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': ['T', 'T', 'F', 'F', 'F', 'T', 'T', 'F', 'F', 'F', 'T', 'T'] }, highlights: { 'dp[0..11] — sums reachable so far (target=11, nums=[1,5,11,5])': [10] }, note: 'Process the second 5: dp[10] also becomes reachable (via dp[5]) — doesn\'t change the answer, since dp[11] was already true. Final: dp[11] = true — the array CAN be partitioned into {1, 5, 5} and {11}.' },
          ],
        },
        code:
`boolean canPartition(int[] nums) {
    int totalSum = 0;
    for (int num : nums) totalSum += num;
    if (totalSum % 2 != 0) return false;
    int target = totalSum / 2;

    boolean[] dp = new boolean[target + 1];
    dp[0] = true;
    for (int num : nums) {
        for (int t = target; t >= num; t--) {   // backwards — each number used at most once
            if (dp[t - num]) dp[t] = true;
        }
    }
    return dp[target];
}`,
        complexity: { time: 'O(n · target)', space: 'O(target)' },
        interviewNote: "The backwards scan is the single most important detail here — scanning forwards would let one number be \"used\" multiple times within the same pass, turning this into an unbounded (rather than 0/1) knapsack by accident.",
        whenToUse: "The moment a problem reduces to \"can some subset sum to exactly X,\" this backwards-scanning 0/1 knapsack DP is the standard tool — the same shape underlies Target Sum and other subset-sum variants.",
      },
    ],
  },
];

export default dp1dProblems;
